import React, { useState, useEffect, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { ColDef } from "ag-grid-community";
import { Search } from "lucide-react";
import {
  Card,
  Button,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Upload,
  Switch,
  Badge,
  Space,
  Divider,
  Row,
  Col,
  Statistic,
  Popover,
  Tag,
  Table,
  message,
} from "antd";
import {
  PlusOutlined,
  UploadOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ExclamationCircleOutlined,
  CalendarOutlined,
  FileTextOutlined,
  BuildOutlined,
  ShopOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { Trash2 } from "lucide-react";
import type { UploadFile } from "antd/es/upload/interface";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import images from "../../../assets/images";
import "../../../styles/ag-grid-custom.css";
import {
  getAllPremises,
  createPremise,
  updatePremise,
  deletePremise,
  searchPremises,
  togglePremiseStatus,
  addIndependentUnit,
  updateIndependentUnit,
  deleteIndependentUnit,
} from "../../../service/api";
import { useLegalContractTypesStore } from "../../../store/store";

const { Option } = Select;
const { TextArea } = Input;

interface IndependentUnit {
  id: string;
  name: string;
  vendor: string;
  location: string;
  ownerName: string;
  ownerPhone: string;
  ownerEmail: string;
  status: "Active" | "Inactive";
  lastUpdated: string;
  contractType?: string;
  contractCertificateName?: string;
  contractCertificateUrl?: string;
  contractCertificateUploadedAt?: string;
  contractCertificate?: {
    url?: string;
    name?: string;
    filename?: string;
    uploadedAt?: string;
  };
}

interface Contract {
  id: string;
  name: string;
  type: "Rental" | "Lease" | "Service";
  startDate: string;
  endDate: string;
  vendor: string;
  amount: number;
  status: "Active" | "Expired" | "Terminated";
  documents: UploadFile[];
}

interface Premises {
  id: string;
  buildingName: string;
  size: string;
  ownership: "Owned" | "Rented" | "Leased";
  address: string;
  facilitiesAvailable: string;
  contracts: Contract[];
  status: "Active" | "Inactive";
  lastUpdated: string;
  hasIndependentUnits: boolean;
  independentUnits: IndependentUnit[];
  // Backend fields mapping
  premisesName?: string;
  addressLine1?: string;
  townCity?: string;
  postcode?: string;
  responsiblePerson?: string;
  responsiblePersonPhoneNumber?: string;
  facilities?: string;
  floor?: number;
  basement?: boolean;
  isStatus?: boolean;
  isEnabled?: boolean;
  region?: string;
  createdDate?: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
  tenantId?: string;
  admin?: string;
  modules?: any[];
  premisesModules?: any[];
  additionalBuildings?: any[];
}

const FacilityInformation: React.FC = () => {
  const [premises, setPremises] = useState<Premises[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalType, setModalType] = useState<string>("");
  const [editingItem, setEditingItem] = useState<any>(null);
  const [form] = Form.useForm();
  const [independentUnitsForm] = Form.useForm();
  const [editUnitForm] = Form.useForm();
  const [showIndependentUnitsForm, setShowIndependentUnitsForm] =
    useState(false);
  const [currentIndependentUnits, setCurrentIndependentUnits] = useState<
    IndependentUnit[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [editingUnit, setEditingUnit] = useState<IndependentUnit | null>(null);
  const [showEditUnitForm, setShowEditUnitForm] = useState(false);
  const [updatingUnit, setUpdatingUnit] = useState(false);
  const [editingUnitContractFile, setEditingUnitContractFile] =
    useState<File | null>(null);
  const [editingUnitContractFileError, setEditingUnitContractFileError] =
    useState<string>("");
  const [editingUnitCertificateRemoved, setEditingUnitCertificateRemoved] =
    useState(false);
  const editUnitFileInputRef = useRef<HTMLInputElement | null>(null);
  const gridRef = useRef<AgGridReact<Premises>>(null);

  const {
    legalContractTypesData,
    fetchLegalContractTypes,
    loading: legalContractTypesLoading,
  } = useLegalContractTypesStore();

  const normalizeIndependentUnit = (unit: any): IndependentUnit => {
    const rawContractCertificate = unit?.contractCertificate;
    const rawDocuments = Array.isArray(unit?.documents) ? unit.documents : [];

    let contractCertificateUrl =
      unit?.contractCertificateUrl ||
      (typeof rawContractCertificate === "string"
        ? rawContractCertificate
        : undefined) ||
      rawContractCertificate?.url ||
      rawContractCertificate?.fileUrl ||
      rawContractCertificate?.path ||
      rawDocuments?.[0]?.url ||
      rawDocuments?.[0]?.fileUrl ||
      undefined;

    let contractCertificateName =
      unit?.contractCertificateName ||
      rawContractCertificate?.name ||
      rawContractCertificate?.filename ||
      rawDocuments?.[0]?.name ||
      rawDocuments?.[0]?.filename ||
      undefined;

    const contractCertificateUploadedAt =
      unit?.contractCertificateUploadedAt ||
      rawContractCertificate?.uploadedAt ||
      rawDocuments?.[0]?.uploadedAt ||
      undefined;

    if (!contractCertificateName && contractCertificateUrl) {
      const decodedUrl = decodeURIComponent(contractCertificateUrl);
      contractCertificateName =
        decodedUrl.split("/").pop() || contractCertificateUrl;
    }

    return {
      ...unit,
      contractType:
        unit?.contractType ||
        unit?.contract_type ||
        unit?.contractTypeId ||
        unit?.contract_type_id,
      contractCertificateName,
      contractCertificateUrl,
      contractCertificateUploadedAt,
    };
  };

  // Fetch premises data
  const fetchPremises = async () => {
    try {
      setLoading(true);
      const response = await getAllPremises();
      const premisesData = response.data || [];

      // Transform backend data to match frontend interface
      const transformedPremises = premisesData.map((premise: any) => ({
        id: premise.id || premise._id,
        buildingName: premise.premisesName || premise.buildingName || "N/A",
        size: premise.size || "N/A",
        ownership: premise.ownership || "Owned",
        address: premise.addressLine1 || premise.address || "N/A",
        facilitiesAvailable:
          premise.facilities || premise.facilitiesAvailable || "N/A",
        status: premise.isStatus ? "Active" : "Inactive",
        lastUpdated:
          premise.lastUpdated || premise.updatedAt || new Date().toISOString(),
        hasIndependentUnits: premise.hasIndependentUnits || false,
        independentUnits: Array.isArray(premise.independentUnits)
          ? premise.independentUnits.map((unit: any) =>
            normalizeIndependentUnit(unit),
          )
          : [],
        contracts: premise.contracts || [],
        // Keep backend fields for form handling
        premisesName: premise.premisesName,
        addressLine1: premise.addressLine1,
        townCity: premise.townCity,
        postcode: premise.postcode,
        responsiblePerson: premise.responsiblePerson,
        responsiblePersonPhoneNumber: premise.responsiblePersonPhoneNumber,
        facilities: premise.facilities,
        floor: premise.floor,
        basement: premise.basement,
        isStatus: premise.isStatus,
        isEnabled: premise.isEnabled,
        region: premise.region,
        createdDate: premise.createdDate,
        updatedAt: premise.updatedAt,
        createdBy: premise.createdBy,
        updatedBy: premise.updatedBy,
        tenantId: premise.tenantId,
        admin: premise.admin,
        modules: premise.modules,
        premisesModules: premise.premisesModules,
        additionalBuildings: premise.additionalBuildings,
      }));

      setPremises(transformedPremises);
    } catch (error) {
      console.error("Error fetching premises:", error);
      message.error("Failed to fetch premises data");
    } finally {
      setLoading(false);
    }
  };

  const handleEditUnitContractFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file =
      event.target.files && event.target.files[0]
        ? event.target.files[0]
        : null;
    setEditingUnitContractFile(file);
    setEditingUnitContractFileError("");
    setEditingUnitCertificateRemoved(false);
  };

  const handleEditUnitContractFileDrop = (
    event: React.DragEvent<HTMLDivElement>,
  ) => {
    event.preventDefault();
    const file =
      event.dataTransfer.files && event.dataTransfer.files[0]
        ? event.dataTransfer.files[0]
        : null;
    if (file) {
      setEditingUnitContractFile(file);
      setEditingUnitContractFileError("");
      setEditingUnitCertificateRemoved(false);
      if (editUnitFileInputRef.current) {
        editUnitFileInputRef.current.value = "";
      }
    }
  };

  const handleEditUnitContractFileDragOver = (
    event: React.DragEvent<HTMLDivElement>,
  ) => {
    event.preventDefault();
  };

  const handleRemoveExistingUnitCertificate = () => {
    setEditingUnitContractFile(null);
    setEditingUnitCertificateRemoved(true);
    setEditingUnitContractFileError("");
    if (editUnitFileInputRef.current) {
      editUnitFileInputRef.current.value = "";
    }
  };

  // Search premises
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      fetchPremises();
      return;
    }

    try {
      setLoading(true);
      const response = await searchPremises(searchQuery);
      const searchResults = response.data || [];

      const transformedResults = searchResults.map((premise: any) => ({
        id: premise.id || premise._id,
        buildingName: premise.premisesName || premise.buildingName || "N/A",
        size: premise.size || "N/A",
        ownership: premise.ownership || "Owned",
        address: premise.addressLine1 || premise.address || "N/A",
        facilitiesAvailable:
          premise.facilities || premise.facilitiesAvailable || "N/A",
        status: premise.isStatus ? "Active" : "Inactive",
        lastUpdated:
          premise.lastUpdated || premise.updatedAt || new Date().toISOString(),
        hasIndependentUnits: premise.hasIndependentUnits || false,
        independentUnits: Array.isArray(premise.independentUnits)
          ? premise.independentUnits.map((unit: any) =>
            normalizeIndependentUnit(unit),
          )
          : [],
        contracts: premise.contracts || [],
        // Keep backend fields
        premisesName: premise.premisesName,
        addressLine1: premise.addressLine1,
        townCity: premise.townCity,
        postcode: premise.postcode,
        responsiblePerson: premise.responsiblePerson,
        responsiblePersonPhoneNumber: premise.responsiblePersonPhoneNumber,
        facilities: premise.facilities,
        floor: premise.floor,
        basement: premise.basement,
        isStatus: premise.isStatus,
        isEnabled: premise.isEnabled,
        region: premise.region,
        createdDate: premise.createdDate,
        updatedAt: premise.updatedAt,
        createdBy: premise.createdBy,
        updatedBy: premise.updatedBy,
        tenantId: premise.tenantId,
        admin: premise.admin,
        modules: premise.modules,
        premisesModules: premise.premisesModules,
        additionalBuildings: premise.additionalBuildings,
      }));

      setPremises(transformedResults);
    } catch (error) {
      console.error("Error searching premises:", error);
      message.error("Failed to search premises");
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchPremises();
    fetchLegalContractTypes();
  }, []);

  // Calculate statistics
  const totalPremises = premises.length;
  const activePremises = premises.filter(
    (premise) => premise.status === "Active",
  ).length;
  const rentedPremises = premises.filter(
    (premise) => premise.ownership === "Rented",
  ).length;
  const premisesWithUnits = premises.filter(
    (premise) => premise.hasIndependentUnits,
  ).length;

  const showModal = (type: string, item?: any) => {
    setModalType(type);
    setEditingItem(item);
    setIsModalVisible(true);
    if (item) {
      form.setFieldsValue({
        premisesName: item.premisesName || item.buildingName,
        size: item.size,
        ownership: item.ownership,
        addressLine1: item.addressLine1 || item.address,
        townCity: item.townCity,
        postcode: item.postcode,
        responsiblePerson: item.responsiblePerson,
        responsiblePersonPhoneNumber: item.responsiblePersonPhoneNumber,
        facilities: item.facilities || item.facilitiesAvailable,
        floor: item.floor,
        basement: item.basement,
        hasIndependentUnits: item.hasIndependentUnits,
      });
      setCurrentIndependentUnits(item.independentUnits || []);
    } else {
      form.resetFields();
      setCurrentIndependentUnits([]);
    }
    setShowIndependentUnitsForm(false);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingItem(null);
    form.resetFields();
    setShowIndependentUnitsForm(false);
    setCurrentIndependentUnits([]);
    setShowEditUnitForm(false);
    setEditingUnit(null);
    setUpdatingUnit(false);
    independentUnitsForm.resetFields();
    editUnitForm.resetFields();
    setEditingUnitContractFile(null);
    setEditingUnitContractFileError("");
    setEditingUnitCertificateRemoved(false);
    if (editUnitFileInputRef.current) {
      editUnitFileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);

      const premisesData = {
        ...values,
        independentUnits: currentIndependentUnits,
        lastUpdated: dayjs().format("YYYY-MM-DD"),
        contracts: editingItem?.contracts || [],
        hasIndependentUnits: currentIndependentUnits.length > 0,
        isStatus: values.isStatus !== undefined ? values.isStatus : true,
        isEnabled: values.isEnabled !== undefined ? values.isEnabled : true,
      };

      if (editingItem) {
        await updatePremise(editingItem.id, premisesData);
        message.success("Facility updated successfully");
      } else {
        await createPremise(premisesData);
        message.success("Facility created successfully");
      }

      fetchPremises();
      handleCancel();
    } catch (error) {
      console.error("Error saving premise:", error);
      message.error("Failed to save facility");
    } finally {
      setLoading(false);
    }
  };

  const handleAddIndependentUnit = async (values: any) => {
    try {
      // Only allow adding independent units when creating new premise
      if (!editingItem) {
        const newUnit: IndependentUnit = {
          id: Date.now().toString(),
          ...values,
          status: "Active",
          lastUpdated: dayjs().format("YYYY-MM-DD"),
        };
        setCurrentIndependentUnits([...currentIndependentUnits, newUnit]);
        message.success("Independent unit added to form");
      }

      independentUnitsForm.resetFields();
    } catch (error) {
      console.error("Error adding independent unit:", error);
      message.error("Failed to add independent unit");
    }
  };

  const handleEditIndependentUnit = (unit: IndependentUnit) => {
    setEditingUnit(unit);
    setShowEditUnitForm(true);
    setEditingUnitContractFile(null);
    setEditingUnitContractFileError("");
    setEditingUnitCertificateRemoved(false);
    if (editUnitFileInputRef.current) {
      editUnitFileInputRef.current.value = "";
    }
    editUnitForm.setFieldsValue({
      name: unit.name,
      vendor: unit.vendor,
      location: unit.location,
      ownerName: unit.ownerName,
      ownerPhone: unit.ownerPhone,
      ownerEmail: unit.ownerEmail,
      contractType: unit.contractType || undefined,
    });
  };

  const handleUpdateIndependentUnit = async (values: any) => {
    try {
      setUpdatingUnit(true);
      setEditingUnitContractFileError("");

      if (
        editingUnitContractFile &&
        editingUnitContractFile.size > 5 * 1024 * 1024
      ) {
        setEditingUnitContractFileError("File size cannot be more than 5MB");
        setUpdatingUnit(false);
        return;
      }

      const payloadValues = {
        ...values,
      };

      if (editingUnitCertificateRemoved && !editingUnitContractFile) {
        (payloadValues as any).contractCertificateRemove = true;
      }

      if (editingItem && editingUnit) {
        let payload: any = payloadValues;

        if (editingUnitContractFile) {
          const formData = new FormData();
          Object.entries(payloadValues).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
              formData.append(key, value as any);
            }
          });
          formData.append("contractCertificate", editingUnitContractFile);
          payload = formData;
        } else if ((payloadValues as any).contractCertificateRemove) {
          const formData = new FormData();
          Object.entries(payloadValues).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
              formData.append(key, value as any);
            }
          });
          payload = formData;
        }

        // For existing premises, call API to update the unit
        const response = await updateIndependentUnit(
          editingItem.id,
          editingUnit.id,
          payload,
        );
        message.success("Independent unit updated successfully");

        let updatedIndependentUnits: IndependentUnit[] = [];
        if (
          response?.data?.independentUnits &&
          Array.isArray(response.data.independentUnits)
        ) {
          updatedIndependentUnits = response.data.independentUnits.map(
            (unit: any) => normalizeIndependentUnit(unit),
          );
        } else {
          updatedIndependentUnits = currentIndependentUnits.map((unit) =>
            unit.id === editingUnit.id
              ? {
                ...unit,
                ...values,
                contractType: values.contractType,
                lastUpdated: dayjs().format("YYYY-MM-DD"),
                ...(editingUnitCertificateRemoved && !editingUnitContractFile
                  ? {
                    contractCertificateName: undefined,
                    contractCertificateUrl: undefined,
                    contractCertificateUploadedAt: undefined,
                  }
                  : editingUnitContractFile
                    ? {
                      contractCertificateName: editingUnitContractFile.name,
                      contractCertificateUrl: undefined,
                      contractCertificateUploadedAt: dayjs().toISOString(),
                    }
                    : {
                      contractCertificateName:
                        editingUnit?.contractCertificateName,
                      contractCertificateUrl:
                        editingUnit?.contractCertificateUrl,
                      contractCertificateUploadedAt:
                        editingUnit?.contractCertificateUploadedAt,
                    }),
              }
              : unit,
          );
        }

        setCurrentIndependentUnits(updatedIndependentUnits);

        // Update the editing item with the new independent units
        setEditingItem({
          ...editingItem,
          independentUnits: updatedIndependentUnits,
        });

        // Update the premises list locally without API call
        setPremises((prevPremises) =>
          prevPremises.map((premise) =>
            premise.id === editingItem.id
              ? { ...premise, independentUnits: updatedIndependentUnits }
              : premise,
          ),
        );
      } else {
        // For new premises, just update local state
        const updatedIndependentUnits = currentIndependentUnits.map((unit) =>
          unit.id === editingUnit?.id
            ? {
              ...unit,
              ...values,
              contractType: values.contractType,
              lastUpdated: dayjs().format("YYYY-MM-DD"),
              ...(editingUnitCertificateRemoved && !editingUnitContractFile
                ? {
                  contractCertificateName: undefined,
                  contractCertificateUrl: undefined,
                  contractCertificateUploadedAt: undefined,
                }
                : editingUnitContractFile
                  ? {
                    contractCertificateName: editingUnitContractFile.name,
                    contractCertificateUrl: undefined,
                    contractCertificateUploadedAt: dayjs().toISOString(),
                  }
                  : {
                    contractCertificateName:
                      editingUnit?.contractCertificateName,
                    contractCertificateUrl:
                      editingUnit?.contractCertificateUrl,
                    contractCertificateUploadedAt:
                      editingUnit?.contractCertificateUploadedAt,
                  }),
            }
            : unit,
        );
        setCurrentIndependentUnits(updatedIndependentUnits);
        message.success("Independent unit updated in form");
      }

      setShowEditUnitForm(false);
      setEditingUnit(null);
      editUnitForm.resetFields();
      setEditingUnitContractFile(null);
      setEditingUnitContractFileError("");
      setEditingUnitCertificateRemoved(false);
      if (editUnitFileInputRef.current) {
        editUnitFileInputRef.current.value = "";
      }
    } catch (error) {
      console.error("Error updating independent unit:", error);
      message.error("Failed to update independent unit");
    } finally {
      setUpdatingUnit(false);
    }
  };

  const removeIndependentUnit = async (unitId: string) => {
    try {
      Modal.confirm({
        title: "Are you sure you want to delete this independent unit?",
        icon: <ExclamationCircleOutlined />,
        content: "This action cannot be undone.",
        okText: "Yes, Delete",
        okType: "danger",
        cancelText: "Cancel",
        onOk: async () => {
          if (editingItem) {
            // For existing premises, call API to delete the unit
            await deleteIndependentUnit(editingItem.id, unitId);
            message.success("Independent unit removed successfully");

            // Update local state directly instead of refetching all premises
            const updatedIndependentUnits = currentIndependentUnits.filter(
              (unit) => unit.id !== unitId,
            );
            setCurrentIndependentUnits(updatedIndependentUnits);

            // Update the editing item with the new independent units
            setEditingItem({
              ...editingItem,
              independentUnits: updatedIndependentUnits,
              hasIndependentUnits: updatedIndependentUnits.length > 0,
            });

            // Update the premises list locally without API call
            setPremises((prevPremises) =>
              prevPremises.map((premise) =>
                premise.id === editingItem.id
                  ? {
                    ...premise,
                    independentUnits: updatedIndependentUnits,
                    hasIndependentUnits: updatedIndependentUnits.length > 0,
                  }
                  : premise,
              ),
            );
          } else {
            // For new premises, just remove from local state
            setCurrentIndependentUnits(
              currentIndependentUnits.filter((unit) => unit.id !== unitId),
            );
            message.success("Independent unit removed from form");
          }
        },
      });
    } catch (error) {
      console.error("Error removing independent unit:", error);
      message.error("Failed to remove independent unit");
    }
  };

  const deletePremises = async (premisesId: string) => {
    try {
      await deletePremise(premisesId);
      message.success("Facility deleted successfully");
      fetchPremises();
    } catch (error) {
      console.error("Error deleting premise:", error);
      message.error("Failed to delete facility");
    }
  };

  const handleEdit = (premise: Premises) => {
    showModal("facility", premise);
  };

  const handleDelete = (premisesId: string) => {
    deletePremises(premisesId);
  };

  const premisesColumns: ColDef<Premises>[] = [
    {
      headerName: "Premises",
      field: "buildingName",
      flex: 1,
      headerClass: "bg-[#e9f5ff]",
      cellStyle: { padding: "10px 10px 0 10px" },
    },
    {
      headerName: "Size(sq ft)",
      field: "size",
      flex: 1,
      headerClass: "bg-[#e9f5ff]",
      cellStyle: { padding: "10px 10px 0 10px" },
    },
    {
      headerName: "Ownership",
      field: "ownership",
      flex: 1,
      headerClass: "bg-[#e9f5ff]",
      cellStyle: { padding: "10px 10px 0 10px" },
      cellRenderer: (params: any) => {
        const ownership = params.value;
        const getColor = (ownership: string) => {
          switch (ownership) {
            case "Owned":
              return "bg-green-100 text-green-800";
            case "Rented":
              return "bg-orange-100 text-orange-800";
            case "Leased":
              return "bg-blue-100 text-blue-800";
            default:
              return "bg-gray-100 text-gray-800";
          }
        };
        return (
          <span
            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getColor(ownership)}`}
          >
            {ownership}
          </span>
        );
      },
    },
    {
      headerName: "Address",
      field: "address",
      flex: 1,
      headerClass: "bg-[#e9f5ff]",
      cellStyle: { padding: "10px 10px 0 10px" },
    },
    {
      headerName: "Facilities Available",
      field: "facilitiesAvailable",
      flex: 1,
      headerClass: "bg-[#e9f5ff]",
      cellStyle: { padding: "10px 10px 0 10px" },
    },
    {
      headerName: "Independent Units",
      field: "hasIndependentUnits",
      flex: 1,
      headerClass: "bg-[#e9f5ff]",
      cellStyle: { padding: "10px 10px 0 10px" },
      cellRenderer: (params: any) => {
        const hasUnits = params.value;
        return hasUnits ? (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Yes
          </span>
        ) : (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            No
          </span>
        );
      },
    },
    {
      headerName: "Status",
      field: "status",
      flex: 1,
      headerClass: "bg-[#e9f5ff]",
      cellStyle: { padding: "10px 10px 0 10px" },
      cellRenderer: (params: any) => {
        const status = params.value;
        return (
          <div className="flex items-center">
            <span
              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${status === "Active"
                  ? "bg-green-100 text-green-800"
                  : "bg-gray-100 text-gray-800"
                }`}
            >
              <span
                className={`w-2 h-2 rounded-full mr-2 ${status === "Active" ? "bg-green-400" : "bg-gray-400"
                  }`}
              ></span>
              {status}
            </span>
          </div>
        );
      },
    },
    {
      headerName: "Last Updated",
      field: "lastUpdated",
      flex: 1,
      headerClass: "bg-[#e9f5ff]",
      cellStyle: { padding: "10px 10px 0 10px" },
      valueFormatter: (params) => {
        return dayjs(params.value).format("DD/MM/YYYY");
      },
    },
    {
      headerName: "Actions",
      flex: 1,
      headerClass: "bg-[#e9f5ff] text-center",
      cellStyle: { padding: "10px 10px 0 10px" },
      cellRenderer: (params: { data: Premises }) => (
        <div className="flex items-center gap-2">
          <Link to={`/premises-information-view/${params.data.id}`}>
            <img className="iconTd" src={images.viewIcon} alt="View" />
          </Link>
          <button onClick={() => handleEdit(params.data)}>
            <img className="iconTd" src={images.editIcon} alt="Edit" />
          </button>
          <button
            onClick={() => handleDelete(params.data.id)}
            aria-label="Delete facility"
          >
            <Trash2 className="text-red-500" size={20} />
          </button>
        </div>
      ),
    },
  ];

  const independentUnitsColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Vendor",
      dataIndex: "vendor",
      key: "vendor",
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
    },
    {
      title: "Owner Name",
      dataIndex: "ownerName",
      key: "ownerName",
    },
    {
      title: "Owner Phone",
      dataIndex: "ownerPhone",
      key: "ownerPhone",
    },
    {
      title: "Owner Email",
      dataIndex: "ownerEmail",
      key: "ownerEmail",
    },
    {
      title: "Actions",
      key: "actions",
      render: (text: string, record: IndependentUnit) => (
        <div className="flex gap-2">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEditIndependentUnit(record)}
            title="Edit unit"
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => removeIndependentUnit(record.id)}
            title="Delete unit"
          />
        </div>
      ),
    },
  ];

  useEffect(() => {
    if (premises.length > 0) {
      setTimeout(() => {
        gridRef.current?.api.sizeColumnsToFit();
      }, 0);
    }
  }, [premises]);

  const hasExistingEditUnitCertificate =
    editingUnit &&
    (editingUnit.contractCertificateName || editingUnit.contractCertificateUrl);
  const showExistingEditUnitCertificate =
    Boolean(hasExistingEditUnitCertificate) &&
    !editingUnitCertificateRemoved &&
    !editingUnitContractFile;
  const showEditUnitCertificateDropzone =
    Boolean(editingUnitContractFile) ||
    editingUnitCertificateRemoved ||
    !hasExistingEditUnitCertificate;

  return (
    <div className="px-2 py-4 max-w-8xl mx-auto mt-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
        <div>
          <h2 className="text-2xl font-bold mb-1">Premises Information</h2>
          <div className="text-gray-500 text-base">
            Manage facility buildings, independent units, and related
            information
          </div>
        </div>
        {/* <button
          className="flex items-center justify-center font-semibold gap-3 bg-[#0fa9a6] text-white px-4 py-2 rounded-full"
          onClick={() => showModal("facility")}
        >
          <img src={images.addPremiessIcon} width={25} alt="" />
          ADD FACILITY
        </button> */}
      </div>

      {/* Statistics Dashboard */}
      <Row gutter={16} className="mb-6">
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Premises"
              value={totalPremises}
              prefix={<BuildOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Active Premises"
              value={activePremises}
              prefix={<BuildOutlined />}
              valueStyle={{ color: "#3f8600" }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Rented Premises"
              value={rentedPremises}
              prefix={<BuildOutlined />}
              valueStyle={{ color: "#faad14" }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Independent Units"
              value={premisesWithUnits}
              prefix={<ShopOutlined />}
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </Col>
      </Row>

      <div className="bg-[#ffffff] rounded-md shadow-md">
        <div className="p-4 flex justify-between items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-500" />
            </div>
            <input
              type="text"
              placeholder="Search"
              className="block w-full pl-10 pr-3 py-2 border-2 border-[#a5b7cf] rounded-md focus:outline-none focus:ring-2 focus:ring-[#0fa9a6] focus:border-transparent"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSearch()}
            />
          </div>
        </div>

        <div className="ag-theme-alpine">
          <AgGridReact
            ref={gridRef}
            rowData={premises}
            columnDefs={premisesColumns}
            rowHeight={50}
            headerHeight={65}
            domLayout="autoHeight"
            pagination={true}
            paginationPageSize={5}
            paginationPageSizeSelector={[5, 10, 20]}
            paginationAutoPageSize={false}
            defaultColDef={{
              sortable: true,
              resizable: true,
            }}
            loadingOverlayComponent={() => <div>Loading...</div>}
            loadingOverlayComponentParams={{}}
            overlayLoadingTemplate="<span class='ag-overlay-loading-center'>Loading...</span>"
            overlayNoRowsTemplate="<span class='ag-overlay-no-rows-center'>No premises found</span>"
          />
        </div>
      </div>

      {/* Facility Modal */}
      <Modal
        title={
          modalType === "facility"
            ? editingItem
              ? "Edit Facility"
              : "Add Facility"
            : "View Facility Details"
        }
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={1000}
        className="rounded-lg overflow-hidden"
        maskClosable={false}
        closable={false}
        zIndex={1000001}
        styles={{
          header: {
            borderBottom: "2px solid #e5e7eb",
            borderRadius: "8px 8px 0 0",
          },
          body: {
            borderRadius: "0",
            padding: "0",
          },
        }}
      >
        {modalType === "viewFacility" && editingItem ? (
          <div className="p-6">
            <div className="space-y-4">
              <h4 className="font-semibold text-lg text-gray-800 mb-4">
                Facility Details
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div>
                    <label className="block text-gray-600 font-semibold text-sm">
                      Building Name
                    </label>
                    <p className="text-gray-800 bg-gray-50 p-2 rounded border">
                      {editingItem.buildingName}
                    </p>
                  </div>
                  <div>
                    <label className="block text-gray-600 font-semibold text-sm">
                      Size (sq ft)
                    </label>
                    <p className="text-gray-800 bg-gray-50 p-2 rounded border">
                      {editingItem.size}
                    </p>
                  </div>
                  <div>
                    <label className="block text-gray-600 font-semibold text-sm">
                      Ownership
                    </label>
                    <p className="text-gray-800 bg-gray-50 p-2 rounded border">
                      {editingItem.ownership}
                    </p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-gray-600 font-semibold text-sm">
                      Address
                    </label>
                    <p className="text-gray-800 bg-gray-50 p-2 rounded border">
                      {editingItem.address}
                    </p>
                  </div>
                  <div>
                    <label className="block text-gray-600 font-semibold text-sm">
                      Facilities Available
                    </label>
                    <p className="text-gray-800 bg-gray-50 p-2 rounded border">
                      {editingItem.facilitiesAvailable}
                    </p>
                  </div>
                  <div>
                    <label className="block text-gray-600 font-semibold text-sm">
                      Status
                    </label>
                    <p className="text-gray-800 bg-gray-50 p-2 rounded border">
                      {editingItem.status}
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-gray-600 font-semibold text-sm">
                  Has Independent Units
                </label>
                <p className="text-gray-800 bg-gray-50 p-2 rounded border">
                  {editingItem.hasIndependentUnits ? "Yes" : "No"}
                </p>
              </div>
              {editingItem.contracts && editingItem.contracts.length > 0 && (
                <div className="mt-4">
                  <label className="block text-gray-600 font-semibold text-sm">
                    Contracts
                  </label>
                  <p className="text-gray-800 bg-gray-50 p-2 rounded border">
                    Contract documents uploaded: {editingItem.contracts.length}{" "}
                    file(s)
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : editingItem ? (
          // Edit Facility Modal - Only edit existing fields
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="premisesName"
                  label="Building Name"
                  rules={[
                    { required: true, message: "Please enter building name" },
                  ]}
                >
                  <Input placeholder="Enter building name" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="size"
                  label="Size(sq ft)"
                  rules={[{ required: true, message: "Please enter size" }]}
                >
                  <Input placeholder="e.g., 50,000 sq ft" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="ownership"
                  label="Ownership"
                  rules={[
                    { required: true, message: "Please select ownership" },
                  ]}
                >
                  <Select placeholder="Select ownership">
                    <Option value="Owned">Owned</Option>
                    <Option value="Rented">Rented</Option>
                    <Option value="Leased">Leased</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="isStatus" label="Status" initialValue={true}>
                  <Switch
                    checkedChildren="Active"
                    unCheckedChildren="Inactive"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="addressLine1"
                  label="Address Line 1"
                  rules={[{ required: true, message: "Please enter address" }]}
                >
                  <Input placeholder="Enter address line 1" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="townCity"
                  label="Town/City"
                  rules={[
                    { required: true, message: "Please enter town/city" },
                  ]}
                >
                  <Input placeholder="Enter town/city" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="postcode"
                  label="Postcode"
                  rules={[{ required: true, message: "Please enter postcode" }]}
                >
                  <Input placeholder="Enter postcode" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="floor"
                  label="Number of Floors"
                  rules={[
                    {
                      required: true,
                      message: "Please enter number of floors",
                    },
                  ]}
                >
                  <Input type="number" placeholder="Enter number of floors" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="responsiblePerson"
                  label="Responsible Person"
                  rules={[
                    {
                      required: true,
                      message: "Please enter responsible person",
                    },
                  ]}
                >
                  <Input placeholder="Enter responsible person name" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="responsiblePersonPhoneNumber"
                  label="Responsible Person Phone"
                  rules={[
                    { required: true, message: "Please enter phone number" },
                  ]}
                >
                  <Input placeholder="Enter phone number" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="facilities"
              label="Facilities Available"
              rules={[{ required: true, message: "Please enter facilities" }]}
            >
              <TextArea
                rows={3}
                placeholder="Enter facilities available (comma-separated)"
              />
            </Form.Item>

            <Form.Item
              name="basement"
              label="Has Basement"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>

            {/* Existing Independent Units Section - Editable in Edit Mode */}
            {currentIndependentUnits.length > 0 && (
              <div className="border rounded-lg p-4 bg-gray-50">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-semibold">Existing Independent Units</h4>
                  <span className="text-sm text-gray-500">
                    Click edit to modify units
                  </span>
                </div>
                <Table
                  dataSource={currentIndependentUnits}
                  columns={independentUnitsColumns}
                  pagination={false}
                  size="small"
                  rowKey="id"
                />
              </div>
            )}

            {/* Edit Independent Unit Form */}
            {showEditUnitForm && editingUnit && (
              <div className="mt-4 border rounded-lg p-4 bg-white">
                <h5 className="font-semibold mb-3">Edit Independent Unit</h5>
                <Form form={editUnitForm} layout="vertical">
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="name"
                        label="Unit Name"
                        rules={[
                          { required: true, message: "Please enter unit name" },
                        ]}
                      >
                        <Input placeholder="e.g., Hospital Cafeteria" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="vendor"
                        label="Vendor"
                        rules={[
                          { required: true, message: "Please enter vendor" },
                        ]}
                      >
                        <Input placeholder="e.g., Food Services Inc." />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="contractType"
                        label="Contract Type"
                        rules={[
                          {
                            required: true,
                            message: "Please select contract type",
                          },
                        ]}
                      >
                        <Select
                          placeholder={
                            legalContractTypesLoading
                              ? "Loading contract types..."
                              : "Select contract type"
                          }
                          loading={legalContractTypesLoading}
                          allowClear
                          showSearch
                          optionFilterProp="children"
                        >
                          {legalContractTypesData?.map((contractType) => (
                            <Option
                              key={contractType.id}
                              value={contractType.id}
                            >
                              {contractType.legalContractName}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item label="Contract Certificate">
                    {showExistingEditUnitCertificate && editingUnit && (
                      <div className="flex items-center justify-between border rounded-md px-3 py-2 mb-3">
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-gray-700">
                            {editingUnit.contractCertificateName ||
                              "Existing certificate"}
                          </span>
                          {editingUnit.contractCertificateUploadedAt && (
                            <span className="text-xs text-gray-500">
                              Uploaded on{" "}
                              {dayjs(
                                editingUnit.contractCertificateUploadedAt,
                              ).format("DD/MM/YYYY")}
                            </span>
                          )}
                        </div>
                        <button
                          type="button"
                          className="text-red-500 hover:text-red-600"
                          onClick={handleRemoveExistingUnitCertificate}
                          title="Remove certificate"
                          aria-label="Remove certificate"
                        >
                          <CloseOutlined />
                        </button>
                      </div>
                    )}

                    <input
                      ref={editUnitFileInputRef}
                      type="file"
                      className="sr-only"
                      accept=".docx,.pdf,.txt,.jpg,.png,.ppt,.pptx"
                      onChange={handleEditUnitContractFileChange}
                      aria-hidden="true"
                      tabIndex={-1}
                    />

                    {showEditUnitCertificateDropzone && (
                      <div
                        className="border-dashed border-2 border-gray-300 rounded-md p-4 text-center cursor-pointer"
                        onClick={() => editUnitFileInputRef.current?.click()}
                        onDragOver={handleEditUnitContractFileDragOver}
                        onDrop={handleEditUnitContractFileDrop}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            editUnitFileInputRef.current?.click();
                          }
                        }}
                      >
                        <span className="block text-sm text-gray-600">
                          {editingUnitContractFile
                            ? editingUnitContractFile.name
                            : "Drag & drop contract certificate here or click to browse"}
                        </span>
                        <span className="block text-xs text-gray-500 mt-1">
                          Supported formats: DOCX, PDF, TXT, JPG, PNG, PPT, PPTX
                          (Max 5MB)
                        </span>
                      </div>
                    )}

                    {editingUnitContractFileError && (
                      <p className="text-red-500 text-sm mt-2">
                        {editingUnitContractFileError}
                      </p>
                    )}
                  </Form.Item>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="location"
                        label="Location"
                        rules={[
                          { required: true, message: "Please enter location" },
                        ]}
                      >
                        <Input placeholder="e.g., Ground Floor, Main Building" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="ownerName"
                        label="Owner Name"
                        rules={[
                          {
                            required: true,
                            message: "Please enter owner name",
                          },
                        ]}
                      >
                        <Input placeholder="e.g., John Smith" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="ownerPhone"
                        label="Owner Phone"
                        rules={[
                          {
                            required: true,
                            message: "Please enter owner phone",
                          },
                        ]}
                      >
                        <Input placeholder="e.g., +1-555-0123" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="ownerEmail"
                        label="Owner Email"
                        rules={[
                          {
                            required: true,
                            message: "Please enter owner email",
                          },
                          {
                            type: "email",
                            message: "Please enter valid email",
                          },
                        ]}
                      >
                        <Input placeholder="e.g., john.smith@example.com" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <div className="flex gap-2">
                    <Button
                      type="primary"
                      loading={updatingUnit}
                      disabled={updatingUnit}
                      onClick={async () => {
                        try {
                          const values = await editUnitForm.validateFields();
                          await handleUpdateIndependentUnit(values);
                        } catch (error) {
                          console.error("Form validation failed:", error);
                        }
                      }}
                    >
                      {updatingUnit ? "Updating..." : "Update Unit"}
                    </Button>
                    <Button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowEditUnitForm(false);
                        setEditingUnit(null);
                        editUnitForm.resetFields();
                        setEditingUnitContractFile(null);
                        setEditingUnitContractFileError("");
                        setEditingUnitCertificateRemoved(false);
                        if (editUnitFileInputRef.current) {
                          editUnitFileInputRef.current.value = "";
                        }
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </Form>
              </div>
            )}

            {/* Contract Upload Section */}
            {(form.getFieldValue("ownership") === "Rented" ||
              form.getFieldValue("ownership") === "Leased") && (
                <Form.Item
                  name="contracts"
                  label="Upload Contracts (if outsourced/leased/rented)"
                >
                  <Upload>
                    <Button icon={<UploadOutlined />}>Upload Contract</Button>
                  </Upload>
                </Form.Item>
              )}
          </Form>
        ) : (
          // Add Facility Modal - Allow adding new independent units
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="premisesName"
                  label="Building Name"
                  rules={[
                    { required: true, message: "Please enter building name" },
                  ]}
                >
                  <Input placeholder="Enter building name" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="size"
                  label="Size(sq ft)"
                  rules={[{ required: true, message: "Please enter size" }]}
                >
                  <Input placeholder="e.g., 50,000 sq ft" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="ownership"
                  label="Ownership"
                  rules={[
                    { required: true, message: "Please select ownership" },
                  ]}
                >
                  <Select placeholder="Select ownership">
                    <Option value="Owned">Owned</Option>
                    <Option value="Rented">Rented</Option>
                    <Option value="Leased">Leased</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="isStatus" label="Status" initialValue={true}>
                  <Switch
                    checkedChildren="Active"
                    unCheckedChildren="Inactive"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="addressLine1"
                  label="Address Line 1"
                  rules={[{ required: true, message: "Please enter address" }]}
                >
                  <Input placeholder="Enter address line 1" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="townCity"
                  label="Town/City"
                  rules={[
                    { required: true, message: "Please enter town/city" },
                  ]}
                >
                  <Input placeholder="Enter town/city" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="postcode"
                  label="Postcode"
                  rules={[{ required: true, message: "Please enter postcode" }]}
                >
                  <Input placeholder="Enter postcode" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="floor"
                  label="Number of Floors"
                  rules={[
                    {
                      required: true,
                      message: "Please enter number of floors",
                    },
                  ]}
                >
                  <Input type="number" placeholder="Enter number of floors" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="responsiblePerson"
                  label="Responsible Person"
                  rules={[
                    {
                      required: true,
                      message: "Please enter responsible person",
                    },
                  ]}
                >
                  <Input placeholder="Enter responsible person name" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="responsiblePersonPhoneNumber"
                  label="Responsible Person Phone"
                  rules={[
                    { required: true, message: "Please enter phone number" },
                  ]}
                >
                  <Input placeholder="Enter phone number" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="facilities"
              label="Facilities Available"
              rules={[{ required: true, message: "Please enter facilities" }]}
            >
              <TextArea
                rows={3}
                placeholder="Enter facilities available (comma-separated)"
              />
            </Form.Item>

            <Form.Item
              name="basement"
              label="Has Basement"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>

            <Form.Item
              name="hasIndependentUnits"
              label="Any units that are independent of the hospital function (coffee shop/book store)"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>

            {/* Independent Units Section - Only in Add Mode */}
            {form.getFieldValue("hasIndependentUnits") && (
              <div className="border rounded-lg p-4 bg-gray-50">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-semibold">Independent Units</h4>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setShowIndependentUnitsForm(true)}
                  >
                    Add Unit
                  </Button>
                </div>

                {currentIndependentUnits.length > 0 && (
                  <Table
                    dataSource={currentIndependentUnits}
                    columns={independentUnitsColumns}
                    pagination={false}
                    size="small"
                    rowKey="id"
                  />
                )}

                {showIndependentUnitsForm && (
                  <div className="mt-4 border rounded-lg p-4 bg-white">
                    <h5 className="font-semibold mb-3">
                      Add New Independent Unit
                    </h5>
                    <Form
                      form={independentUnitsForm}
                      layout="vertical"
                      onFinish={handleAddIndependentUnit}
                    >
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            name="name"
                            label="Unit Name"
                            rules={[
                              {
                                required: true,
                                message: "Please enter unit name",
                              },
                            ]}
                          >
                            <Input placeholder="e.g., Hospital Cafeteria" />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="vendor"
                            label="Vendor"
                            rules={[
                              {
                                required: true,
                                message: "Please enter vendor",
                              },
                            ]}
                          >
                            <Input placeholder="e.g., Food Services Inc." />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            name="location"
                            label="Location"
                            rules={[
                              {
                                required: true,
                                message: "Please enter location",
                              },
                            ]}
                          >
                            <Input placeholder="e.g., Ground Floor, Main Building" />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="ownerName"
                            label="Owner Name"
                            rules={[
                              {
                                required: true,
                                message: "Please enter owner name",
                              },
                            ]}
                          >
                            <Input placeholder="e.g., John Smith" />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            name="ownerPhone"
                            label="Owner Phone"
                            rules={[
                              {
                                required: true,
                                message: "Please enter owner phone",
                              },
                            ]}
                          >
                            <Input placeholder="e.g., +1-555-0123" />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="ownerEmail"
                            label="Owner Email"
                            rules={[
                              {
                                required: true,
                                message: "Please enter owner email",
                              },
                              {
                                type: "email",
                                message: "Please enter valid email",
                              },
                            ]}
                          >
                            <Input placeholder="e.g., john.smith@example.com" />
                          </Form.Item>
                        </Col>
                      </Row>

                      <div className="flex gap-2">
                        <Button type="primary" htmlType="submit">
                          Add Unit
                        </Button>
                        <Button
                          onClick={() => setShowIndependentUnitsForm(false)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </Form>
                  </div>
                )}
              </div>
            )}

            {/* Contract Upload Section */}
            {(form.getFieldValue("ownership") === "Rented" ||
              form.getFieldValue("ownership") === "Leased") && (
                <Form.Item
                  name="contracts"
                  label="Upload Contracts (if outsourced/leased/rented)"
                >
                  <Upload>
                    <Button icon={<UploadOutlined />}>Upload Contract</Button>
                  </Upload>
                </Form.Item>
              )}
          </Form>
        )}

        {/* Custom Footer with Styled Buttons */}
        <div className="flex bg-[#61748E] px-3 py-2 justify-end gap-4 -mx-6 -mb-6">
          {modalType === "viewFacility" ? (
            <button
              onClick={handleCancel}
              className="px-4 py-2 bg-teal-500 text-white rounded-full hover:bg-teal-600 transition-colors"
            >
              Close
            </button>
          ) : (
            <>
              <button
                onClick={handleCancel}
                className="px-4 py-2 bg-gray-400 text-white rounded-full hover:bg-gray-500 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => form.submit()}
                disabled={loading}
                className="px-4 py-2 bg-teal-500 text-white rounded-full hover:bg-teal-600 transition-colors disabled:opacity-50"
              >
                {loading
                  ? editingItem
                    ? "Updating..."
                    : "Adding..."
                  : editingItem
                    ? "Update"
                    : "Add"}
              </button>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default FacilityInformation;
